import * as THREE from 'three'
import TweenLite from 'gsap/TweenLite'
import { Power3 } from 'gsap/EasePack'
import OBJLoader from './OBJLoader/'

const modelsVertexShader = require('./shaders/models/vertex.glsl')
const modelsFragmentShader = require('./shaders/models/fragment.glsl')

const tableVertexShader = require('./shaders/table/vertex.glsl')
const tableFragmentShader = require('./shaders/table/fragment.glsl')

export default class Grid {
    /**
     * Constructor
     */
    constructor(_options) {
        this.time = _options.time
        this.sizes = _options.sizes
        this.cursor = _options.cursor
        this.renderer = _options.renderer
        this.hasTexture = _options.hasTexture
        this.level = 0

        this.name = 'models'
        this.hasLevels = true

        if (this.hasTexture) {
            this.renderTarget = new THREE.WebGLRenderTarget(this.sizes.width, this.sizes.height, { minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter, format: THREE.RGBAFormat })
            this.texture = this.renderTarget.texture
        }

        this.setScene()
        this.setModels()
    }

    /**
     * Set scene
     */
    setScene() {
        // Scene
        this.scene = new THREE.Scene()

        // Camera
        this.camera = new THREE.PerspectiveCamera(40, this.sizes.width / this.sizes.height, 0.1, 1000)
        this.camera.position.x = 0
        this.camera.position.y = 0
        this.camera.position.z = 3
        this.camera.rotation.order = 'YXZ'

        this.mouse = {}
        this.mouse.x = 0.5
        this.mouse.y = 0.5

        // Target
        this.target = new THREE.Vector3()
    }

    /**
     * Set plane
     */
    setModels() {
        // Set up
        this.models = {}
        this.models.container = new THREE.Object3D()
        this.models.container.position.y = -0.1
        this.models.container.position.x = 0.4
        this.models.container.rotation.y = 0.4
        this.models.container.rotation.x = 0.2
        this.models.container.scale.set(2, 2, 2)
        this.scene.add(this.models.container)

        this.models.planes = {}
        this.models.planes.subdivision = 8
        this.models.planes.canvasResolution = Math.pow(2, 11)

        // Canvas
        this.models.planes.$canvas = document.createElement('canvas')
        this.models.planes.$canvas.width = this.models.planes.canvasResolution
        this.models.planes.$canvas.height = this.models.planes.canvasResolution
        // this.models.planes.$canvas.style.position = 'absolute'
        // this.models.planes.$canvas.style.zIndex = 999
        // this.models.planes.$canvas.style.top = '10px'
        // this.models.planes.$canvas.style.left = '10px'
        // document.body.appendChild(this.models.planes.$canvas)

        // Context
        this.models.planes.context = this.models.planes.$canvas.getContext('2d')

        // Draw black background
        this.models.planes.context.fillRect(0, 0, this.models.planes.canvasResolution, this.models.planes.canvasResolution)

        // Draw lines on context
        this.models.planes.context.lineWidth = 2
        this.models.planes.context.strokeStyle = '#ffffff'
        const cellSize = this.models.planes.canvasResolution / this.models.planes.subdivision
        this.models.planes.context.beginPath()
        for (let i = 0; i < this.models.planes.subdivision + 1; i++) {
            // Vertical lines
            let x = Math.round(i * cellSize) - 0.5
            if (x <= 0) {
                x = 0.5
            }
            this.models.planes.context.moveTo(x, 0)
            this.models.planes.context.lineTo(x, this.models.planes.canvasResolution)

            // Horizontal lines
            let y = Math.round(i * cellSize) - 0.5
            if (y <= 0) {
                y = 0.5
            }
            this.models.planes.context.moveTo(0, y)
            this.models.planes.context.lineTo(this.models.planes.canvasResolution, y)
        }
        this.models.planes.context.stroke()

        this.models.planes.context.globalAlpha = 0.5
        this.models.planes.context.lineWidth = 1
        for (let i = 0; i < this.models.planes.subdivision * 2 + 1; i++) {
            // Diagonal lines
            this.models.planes.context.moveTo(Math.round(i * cellSize) - 0.5, 0)
            this.models.planes.context.lineTo(Math.round(i * cellSize) - 0.5 - this.models.planes.canvasResolution, this.models.planes.canvasResolution)

            this.models.planes.context.moveTo(Math.round(i * cellSize) - 0.5 - this.models.planes.canvasResolution, 0)
            this.models.planes.context.lineTo(Math.round(i * cellSize) - 0.5, this.models.planes.canvasResolution)
        }
        this.models.planes.context.stroke()

        // Draw discs
        this.models.planes.context.fillStyle = '#ffffff'
        this.models.planes.context.globalAlpha = 1
        for (let i = 0; i < this.models.planes.subdivision + 1; i++) {
            for (let j = 0; j < this.models.planes.subdivision + 1; j++) {
                this.models.planes.context.beginPath()
                this.models.planes.context.arc(Math.round(i * cellSize), Math.round(j * cellSize), 4, 0, Math.PI * 2)
                this.models.planes.context.fill()
            }
        }

        // Texture
        this.models.planes.texture = new THREE.Texture(this.models.planes.$canvas)
        this.models.planes.texture.needsUpdate = true

        const maxAnisotropy = this.renderer.capabilities.getMaxAnisotropy()
        this.models.planes.texture.anisotropy = maxAnisotropy

        // Geometry
        this.models.planes.geometry = new THREE.PlaneGeometry(1, 1, this.models.planes.subdivision, this.models.planes.subdivision)

        // Floor
        this.models.planes.floor = new THREE.Mesh(this.models.planes.geometry, this.getPlaneMaterial(this.models.planes.texture))
        this.models.planes.floor.rotation.x = -Math.PI * 0.5
        this.models.container.add(this.models.planes.floor)

        // Wall 1
        this.models.planes.wall1 = new THREE.Mesh(this.models.planes.geometry, this.getPlaneMaterial(this.models.planes.texture))
        this.models.planes.wall1.position.y = 0.5
        this.models.planes.wall1.position.z = -0.5
        this.models.container.add(this.models.planes.wall1)

        // Wall 2
        this.models.planes.wall2 = new THREE.Mesh(this.models.planes.geometry, this.getPlaneMaterial(this.models.planes.texture))
        this.models.planes.wall2.rotation.y = -Math.PI * 0.5
        this.models.planes.wall2.position.y = 0.5
        this.models.planes.wall2.position.x = 0.5
        this.models.container.add(this.models.planes.wall2)

        // Table
        const objLoader = new OBJLoader()
        objLoader.load('/static/3d/table.obj', data => {
            // Geometry
            const geometry = data.children[0].geometry

            // Material
            // const material = new THREE.MeshBasicMaterial({ color: 0xffffff, wireframe: true })
            const material = this.getTableMaterial()
            material.wireframe = true

            this.models.table = new THREE.Mesh(geometry, material)
            this.models.table.scale.set(0.0002, 0.0002, 0.0002)
            this.models.container.add(this.models.table)

            // Initial state
            this.models.table.material.uniforms.uAlpha.value = 0
        })

        // Initial state
        this.models.planes.floor.material.uniforms.uAlpha.value = 1
        this.models.planes.wall1.material.uniforms.uAlpha.value = 0
        this.models.planes.wall2.material.uniforms.uAlpha.value = 0
    }

    getTableMaterial() {
        return new THREE.ShaderMaterial({
            flatShading: false,
            transparent: true,
            uniforms:
            {
                uAlpha: { type: 'f', value: 1 },
                uProgress: { type: 'f', value: 0.3 }
            },
            vertexShader: tableVertexShader,
            fragmentShader: tableFragmentShader
        })
    }

    getPlaneMaterial(texture) {
        return new THREE.ShaderMaterial({
            flatShading: false,
            transparent: true,
            uniforms:
            {
                uTexture: { type: 't', value: texture },
                uAlpha: { type: 'f', value: 1 },
                uTime: { type: 'f', value: 0 },
                uWaving: { type: 'f', value: 0 }, // 0.02
                uElevation: { type: 'f', value: 0 } // 0.2
            },
            vertexShader: modelsVertexShader,
            fragmentShader: modelsFragmentShader
        })
    }

    /**
     * Go to level
     */
    goToLevel(index) {
        // Table not loaded yet
        if (!this.models.table) {
            return
        }
        
        switch (index) {
        case 0:
            // Floor
            TweenLite.to(this.models.planes.floor.material.uniforms.uWaving, 0.75, { value: 0, ease: Power3.easeInOut })
            TweenLite.to(this.models.planes.floor.material.uniforms.uElevation, 0.75, { value: 0, ease: Power3.easeInOut })

            // Walls
            TweenLite.to(this.models.planes.wall1.position, 0.75, { z: -0.8, ease: Power3.easeIn })
            TweenLite.to(this.models.planes.wall1.material.uniforms.uAlpha, 0.75, { value: 0, ease: Power3.easeIn })
            TweenLite.to(this.models.planes.wall2.position, 0.75, { x: 0.8, ease: Power3.easeIn })
            TweenLite.to(this.models.planes.wall2.material.uniforms.uAlpha, 0.75, { value: 0, ease: Power3.easeIn })

            // Table
            TweenLite.to(this.models.table.position, 0.75, { y: 0.1, ease: Power3.easeIn })
            TweenLite.to(this.models.table.material.uniforms.uAlpha, 0.75, { value: 0, ease: Power3.easeIn })
            TweenLite.to(this.models.table.material.uniforms.uProgress, 1, { value: 0.3, delay: 0, ease: Power3.easeOut })
            break
            
        case 1:
            // Floor
            TweenLite.to(this.models.planes.floor.material.uniforms.uWaving, 0.75, { value: 0.02, ease: Power3.easeInOut })
            TweenLite.to(this.models.planes.floor.material.uniforms.uElevation, 0.75, { value: 0.2, ease: Power3.easeInOut })

            // Walls
            TweenLite.to(this.models.planes.wall1.position, 0.75, { z: -0.8, ease: Power3.easeIn })
            TweenLite.to(this.models.planes.wall1.material.uniforms.uAlpha, 0.75, { value: 0, ease: Power3.easeIn })
            TweenLite.to(this.models.planes.wall2.position, 0.75, { x: 0.8, ease: Power3.easeIn })
            TweenLite.to(this.models.planes.wall2.material.uniforms.uAlpha, 0.75, { value: 0, ease: Power3.easeIn })

            // Table
            TweenLite.to(this.models.table.position, 0.75, { y: 0.1, ease: Power3.easeIn })
            TweenLite.to(this.models.table.material.uniforms.uAlpha, 0.75, { value: 0, ease: Power3.easeIn })
            TweenLite.to(this.models.table.material.uniforms.uProgress, 1, { value: 0.3, delay: 0, ease: Power3.easeOut })
            break
            
        case 2:
            // Floor
            TweenLite.to(this.models.planes.floor.material.uniforms.uWaving, 0.75, { value: 0, delay: this.level === 3 ? 0.25 : 0, ease: Power3.easeInOut })
            TweenLite.to(this.models.planes.floor.material.uniforms.uElevation, 0.75, { value: 0, delay: this.level === 3 ? 0.25 : 0, ease: Power3.easeInOut })

            // Walls
            TweenLite.to(this.models.planes.wall1.position, 0.75, { z: -0.5, delay: 0.35, ease: Power3.easeOut })
            TweenLite.to(this.models.planes.wall1.material.uniforms.uAlpha, 0.75, { value: 1, delay: 0.35, ease: Power3.easeOut })
            TweenLite.to(this.models.planes.wall2.position, 0.75, { x: 0.5, delay: 0.55, ease: Power3.easeOut })
            TweenLite.to(this.models.planes.wall2.material.uniforms.uAlpha, 0.75, { value: 1, delay: 0.55, ease: Power3.easeOut })

            // Table
            TweenLite.to(this.models.table.position, 0.75, { y: 0, delay: 0.75, ease: Power3.easeOut })
            TweenLite.to(this.models.table.material.uniforms.uAlpha, 0.75, { value: 1, delay: 0.75, ease: Power3.easeOut })
            TweenLite.to(this.models.table.material.uniforms.uProgress, 1, { value: 0.3, delay: 0, ease: Power3.easeOut })
            break
            
        case 3:
            // Floor
            TweenLite.to(this.models.planes.floor.material.uniforms.uWaving, 0.75, { value: 0, ease: Power3.easeInOut })
            TweenLite.to(this.models.planes.floor.material.uniforms.uElevation, 0.75, { value: 0, ease: Power3.easeInOut })

            // Walls
            TweenLite.to(this.models.planes.wall1.position, 0.75, { z: -0.5, delay: 0.35, ease: Power3.easeOut })
            TweenLite.to(this.models.planes.wall1.material.uniforms.uAlpha, 0.75, { value: 1, delay: 0.35, ease: Power3.easeOut })
            TweenLite.to(this.models.planes.wall2.position, 0.75, { x: 0.5, delay: 0.55, ease: Power3.easeOut })
            TweenLite.to(this.models.planes.wall2.material.uniforms.uAlpha, 0.75, { value: 1, delay: 0.55, ease: Power3.easeOut })

            // Table
            TweenLite.to(this.models.table.position, 0.75, { y: 0, delay: 0.75, ease: Power3.easeOut })
            TweenLite.to(this.models.table.material.uniforms.uAlpha, 0.75, { value: 1, delay: 0.75, ease: Power3.easeOut })
            TweenLite.to(this.models.table.material.uniforms.uProgress, 1, { value: 1.1, delay: 0, ease: Power3.easeOut })
            break

        default:
            break
        }

        this.level = index
    }

    /**
     * Resize
     */
    resize() {
        // Camera
        this.camera.aspect = this.sizes.width / this.sizes.height
        this.camera.updateProjectionMatrix()

        // Render target
        if (this.hasTexture) {
            this.renderTarget.setSize(this.sizes.width, this.sizes.height)
        }
    }

    /**
     * Update
     */
    update() {
        // Mouse interaction
        this.mouse.x += (this.cursor.fromCenter.x - this.mouse.x) * 0.005 * this.time.delta
        this.mouse.y += (this.cursor.fromCenter.y - this.mouse.y) * 0.005 * this.time.delta

        this.camera.position.x = this.mouse.x * 0.5
        this.camera.position.y = -this.mouse.y * 0.5

        this.camera.lookAt(this.target)

        // Planes
        this.models.planes.floor.material.uniforms.uTime.value = this.time.elapsed
        this.models.planes.wall1.material.uniforms.uTime.value = this.time.elapsed
        this.models.planes.wall2.material.uniforms.uTime.value = this.time.elapsed

        // Render
        this.render()
    }

    /**
     * Render
     */
    render() {
        if (this.hasTexture) {
            this.renderer.render(this.scene, this.camera, this.renderTarget)
        } else {
            this.renderer.render(this.scene, this.camera)
        }
    }

    /**
     * Dispose
     */
    dispose() {
        if (this.hasTexture) {
            this.texture.dispose()
        }
    }
}



// WEBPACK FOOTER //
// ./src/scripts/libs/experience/Models.js